import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Initials } from "../components/PeoplePicker";
import { AddMeetings, AddPeople, ProjectForm, shortDay } from "../components/ProjectDialogs";
import { ProjectOverview, ProjectTimeline } from "../components/ProjectInsights";
import { ProjectSearch } from "../components/ProjectSearch";
import { RenameMeeting } from "../components/RenameMeeting";
import { RowMenu } from "../components/RowMenu";
import { useClickOutside } from "../components/Shell";
import { endpoints, type ProjectDetail } from "../lib/api";
import { meetingPlatformKey, platformLabel } from "../lib/calendar";
import { duration } from "../lib/format";

type Row = ProjectDetail["meetings"][number];

/** One project: its numbers, its people, and its meetings. Everyone in it can
 * open every meeting in it; the person who made it decides who's in. */
export function ProjectPage() {
  const id = Number(useParams().projectId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });
  const csrfToken = me.data?.csrf_token ?? "";
  const { data: project, error, isLoading } = useQuery({
    queryKey: ["project", id],
    queryFn: () => endpoints.project(id),
    enabled: Number.isFinite(id),
  });
  const [dialog, setDialog] = useState<"edit" | "people" | "meetings" | null>(null);
  // ?tab=overview or ?tab=timeline, so a link or a refresh lands on the same tab.
  const [params, setParams] = useSearchParams();
  const tab = (["overview", "timeline"] as const).find((t) => t === params.get("tab")) ?? "meetings";
  // ?q=…: a search, shown in place of the tab while there is one.
  const query = (params.get("q") ?? "").trim();
  const [typed, setTyped] = useState(params.get("q") ?? "");
  useEffect(() => {
    // Searched a moment after typing stops, not on every key.
    const timer = setTimeout(() => {
      if (typed.trim() === query) return;
      setParams((was) => {
        const next = new URLSearchParams(was);
        if (typed.trim()) next.set("q", typed.trim());
        else next.delete("q");
        return next;
      }, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [typed, query, setParams]);
  const searching = query.length >= 2;

  const saved = (next: ProjectDetail | { left: true } | void) => {
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["recordings"] });
    queryClient.invalidateQueries({ queryKey: ["project-insights", id] });
    if (next && "id" in next) queryClient.setQueryData(["project", id], next);
  };
  const removeMember = useMutation({
    mutationFn: (userId: number) => endpoints.removeProjectMember(id, userId, csrfToken),
    onSuccess: (next) => {
      saved(next);
      if ("left" in next) navigate("/projects");
    },
  });
  const removeMeeting = useMutation({
    mutationFn: (sessionId: string) => endpoints.removeProjectMeeting(id, sessionId, csrfToken),
    onSuccess: saved,
  });
  const remove = useMutation({
    mutationFn: () => endpoints.deleteProject(id, csrfToken),
    onSuccess: () => {
      saved();
      queryClient.removeQueries({ queryKey: ["project", id] });
      navigate("/projects");
    },
  });

  if (error || isLoading || !project) {
    return (
      <div className="page-body">
        {error && <div className="error">{(error as Error).message}</div>}
        {isLoading && <div className="empty">Loading…</div>}
      </div>
    );
  }

  const you = project.members.find((m) => m.you);
  const failed = (removeMember.error ?? removeMeeting.error ?? remove.error) as Error | null;
  return (
    <div className="page-body project-page">
      <div className="doc-crumbs">
        <Link to="/projects">Projects</Link>
        <span aria-hidden>/</span>
      </div>
      <header className="project-head">
        <div className="project-head-text">
          <h1>{project.name}</h1>
          <p className="meetings-sub">
            {project.description && <>{project.description} · </>}
            made by {project.created_by_name ?? "someone who has left"}
          </p>
        </div>
        <div className="project-actions">
          <ProjectMenu
            canManage={project.can_manage}
            canLeave={Boolean(you && !you.creator)}
            onEdit={() => setDialog("edit")}
            onDelete={() => {
              if (window.confirm(`Delete “${project.name}”? Its meetings stay, but people who could only open `
                + "them through this project lose them.")) remove.mutate();
            }}
            onLeave={() => {
              if (you && window.confirm(`Leave “${project.name}”? You'll lose the meetings you could only open `
                + "through it.")) removeMember.mutate(you.id);
            }}
          />
          <button className="btn-primary" onClick={() => setDialog("meetings")}>
            <span aria-hidden>+</span> Add meetings
          </button>
        </div>
      </header>

      {failed && <div className="error">{failed.message}</div>}

      <dl className="project-metrics">
        <div>
          <dt>Meetings</dt>
          <dd>{project.metrics.meetings}</dd>
        </div>
        <div>
          <dt>Hours recorded</dt>
          <dd>{hours(project.metrics.seconds)}</dd>
        </div>
        <div>
          <dt>Action items</dt>
          <dd>{project.metrics.action_items}</dd>
        </div>
      </dl>

      <section className="project-people" aria-label="People">
        <span className="doc-label">People</span>
        <ul>
          {project.members.map((member) => (
            <li key={member.id}>
              <Initials name={member.name ?? member.email} />
              <span>
                {member.name ?? member.email}
                {member.you && <span className="share-you"> (you)</span>}
                {member.creator && <small>made it</small>}
              </span>
              {project.can_manage && !member.creator && (
                <button className="person-remove" aria-label={`Take ${member.name ?? member.email} off the project`}
                        title="Take off the project" disabled={removeMember.isPending}
                        onClick={() => removeMember.mutate(member.id)}>
                  ✕
                </button>
              )}
            </li>
          ))}
          {project.can_manage && (
            <li>
              <button className="btn-quiet" onClick={() => setDialog("people")}>+ Add people</button>
            </li>
          )}
        </ul>
      </section>

      <div className="project-tabs-row">
        <div className="segmented project-tabs" role="tablist" aria-label="Project">
          {([["meetings", "Meetings"], ["overview", "Overview"], ["timeline", "Timeline"]] as const).map(([key, label]) => (
            <button key={key} role="tab" aria-selected={!searching && tab === key}
                    className={!searching && tab === key ? "on" : ""}
                    onClick={() => {
                      setTyped("");  // a tab clicked ends the search
                      setParams(key === "meetings" ? {} : { tab: key }, { replace: true });
                    }}>
              {label}
              {key === "meetings" && <span className="seg-count">{project.meetings.length}</span>}
            </button>
          ))}
        </div>
        <label className="project-search-box">
          <span className="sr-only">Search this project</span>
          <input type="search" value={typed} placeholder="Search this project" maxLength={200}
                 onChange={(event) => setTyped(event.target.value)}
                 onKeyDown={(event) => event.key === "Escape" && setTyped("")} />
        </label>
      </div>

      {searching && <ProjectSearch projectId={id} query={query} />}
      {!searching && tab === "overview" && <ProjectOverview projectId={id} csrfToken={csrfToken} />}
      {!searching && tab === "timeline" && <ProjectTimeline projectId={id} csrfToken={csrfToken} />}
      {!searching && tab === "meetings" && (
      <section className="card meetings-card project-meetings">
        {project.meetings.length === 0 ? (
          <div className="project-empty-meetings">
            <p>No meetings in this project yet.</p>
            <button className="btn-secondary" onClick={() => setDialog("meetings")}>Add meetings</button>
          </div>
        ) : (
          project.meetings.map((meeting) => (
            <MeetingRow key={meeting.session_id} meeting={meeting} csrfToken={csrfToken}
                        removing={removeMeeting.isPending}
                        onRemove={() => removeMeeting.mutate(meeting.session_id!)} />
          ))
        )}
      </section>
      )}

      {dialog === "edit" && (
        <ProjectForm project={project} csrfToken={csrfToken} onClose={() => setDialog(null)}
                     onSaved={() => setDialog(null)} />
      )}
      {dialog === "people" && <AddPeople project={project} csrfToken={csrfToken} onClose={() => setDialog(null)} />}
      {dialog === "meetings" && <AddMeetings project={project} csrfToken={csrfToken} onClose={() => setDialog(null)} />}
    </div>
  );
}

function MeetingRow({ meeting, csrfToken, removing, onRemove }: {
  meeting: Row;
  csrfToken: string;
  removing: boolean;
  onRemove: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const cells = (
    <>
      <span className="m-main">
        {renaming && meeting.session_id ? (
          <RenameMeeting sessionId={meeting.session_id} title={meeting.title ?? ""} csrfToken={csrfToken}
                         onDone={() => setRenaming(false)} />
        ) : (
          <span className="dash-title">{meeting.title ?? "Untitled meeting"}</span>
        )}
        <span className="m-meta">
          <span className="event-platform">{platformLabel(meeting)}</span>
          {meeting.added_by && <span>added by {meeting.added_by}</span>}
        </span>
      </span>
      <span className="project-row-when">{shortDay(meeting.starts_at ?? meeting.requested_at)}</span>
      <span className="dash-num">{meeting.duration_seconds ? duration(meeting.duration_seconds) : "—"}</span>
      <Status meeting={meeting} />
    </>
  );
  return (
    <div className={`project-row p-${meetingPlatformKey(meeting) ?? (meeting.in_person ? "in_person" : "none")}`}>
      {/* While the name is being edited the row isn't a link, so typing in it can't open the meeting. */}
      {renaming ? <div className="project-row-link editing">{cells}</div> : (
        <Link className="project-row-link" to={`/meetings/${meeting.session_id}`}>{cells}</Link>
      )}
      <RowMenu items={[
        ...(meeting.can_rename ? [{ label: "Rename", onClick: () => setRenaming(true) }] : []),
        ...(meeting.can_remove && !removing
          ? [{ label: "Remove from project", onClick: onRemove, danger: true }] : []),
      ]} />
    </div>
  );
}

/** Ready, on its way, or why there's nothing — as the Meetings page says it. */
function Status({ meeting }: { meeting: Row }) {
  if (meeting.state === "failed" || meeting.state === "not_admitted") return <span className="status failed">{meeting.label}</span>;
  if (meeting.transcript === "ready") return <span className="status ready">Ready</span>;
  if (meeting.active || meeting.transcript === "pending") {
    return (
      <span className="status waiting">
        <span className="status-dot" aria-hidden />
        {meeting.active ? meeting.label : "Transcribing…"}
      </span>
    );
  }
  return <span className="status">{meeting.state === "cancelled" ? "Cancelled" : "No transcript"}</span>;
}

/** ⋯: edit and delete for whoever made it (and admins); leave for everyone else. */
function ProjectMenu({ canManage, canLeave, onEdit, onDelete, onLeave }: {
  canManage: boolean;
  canLeave: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onLeave: () => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useClickOutside(box, open, () => setOpen(false));
  if (!canManage && !canLeave) return null;
  const pick = (action: () => void) => () => {
    setOpen(false);
    action();
  };
  return (
    <div className="project-menu" ref={box}>
      <button className="btn-quiet project-more" aria-label="More" aria-haspopup="menu" aria-expanded={open}
              onClick={() => setOpen((was) => !was)}>
        ⋯
      </button>
      {open && (
        <div className="menu" role="menu">
          {canManage && <button role="menuitem" className="menu-item" onClick={pick(onEdit)}>Rename or edit</button>}
          {canManage && <button role="menuitem" className="menu-item danger" onClick={pick(onDelete)}>Delete project</button>}
          {canLeave && <button role="menuitem" className="menu-item danger" onClick={pick(onLeave)}>Leave project</button>}
        </div>
      )}
    </div>
  );
}

/** 9.5, or 0.3 for a short one; whole hours past ten. */
function hours(seconds: number): string {
  const value = seconds / 3600;
  if (value === 0) return "0";
  return value >= 10 ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, "");
}
